import logo from '@/assets/images/logo.png';
import useUserStore from '@/stores/UserStore';
import {
  Activity,
  BarChart2,
  Bot,
  Contact,
  GitBranch,
  MailWarning,
  MessageSquareMore,
  Settings,
} from 'lucide-react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import useFeaturebase from '@/hooks/useFeaturebase';

import FeaturebaseFeedbackWidget from '../common/FeaturebaseFeedbackWidget';
import NotificationsPopover from '../common/NotificationsPopover';
import { ModeToggle } from '../common/ThemeToggle';
import { UserAvatarMenu } from './UserAvatarMenu';

export default function Navbar() {
  useFeaturebase();
  const workflowEnabled = useUserStore((state) => state.workflowEnabled);
  const igOpensAvailable = useUserStore((state) => state.igOpensAvailable);
  const salesBotEnabled = useUserStore((state) => state.salesBotEnabled);
  const navigation = [
    {
      name: 'Inbox',
      href: 'inbox',
      icon: MessageSquareMore,
      current: true,
    },
    {
      name: 'Contacts',
      href: 'contacts',
      icon: Contact,
      current: false,
    },
    {
      name: 'Agent Activity Chart',
      href: 'agent-activity',
      icon: BarChart2,
      current: false,
    },
    {
      name: 'Reports',
      href: 'reports',
      icon: Activity,
      count: '12',
      current: false,
    },

    ...(igOpensAvailable
      ? [
          {
            name: 'Opens',
            href: 'followers',
            icon: MailWarning,
            current: false,
          },
        ]
      : []),
    ...(workflowEnabled
      ? [
          {
            name: 'Workflows',
            href: 'workflows',
            icon: GitBranch,
            current: false,
          },
        ]
      : []),
    ...(salesBotEnabled
      ? [
          {
            name: 'Revio Autopilot',
            href: 'revio-autopilot',
            icon: Bot,
            current: false,
          },
        ]
      : []),
    {
      name: 'Settings',
      href: 'settings',
      icon: Settings,
      count: '20+',
      current: false,
    },
  ];

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
  }

  const { pathname } = useLocation();
  const currentPath = pathname.split('/');
  const firstSegment = currentPath[1] || '';

  return (
    <>
      <div className='fixed inset-y-0 z-10 flex w-14 flex-col'>
        <div className='flex h-screen grow flex-col items-center justify-between overflow-y-auto border-r bg-background px-2'>
          <div className='flex flex-col items-center'>
            <div className='flex h-14 shrink-0 items-center [@media(max-height:800px)]:h-12'>
              <img
                className='h-8 w-auto [@media(max-height:800px)]:h-7'
                src={logo}
                alt='Your Company'
              />
            </div>
            <nav className='flex min-h-0 flex-col pt-2 [@media(max-height:800px)]:pt-1'>
              <ul
                role='list'
                className='flex min-h-0 flex-col items-center gap-y-3 [@media(max-height:800px)]:gap-y-1.5'
              >
                <li>
                  <ul
                    role='list'
                    className='-mx-2 space-y-3 [@media(max-height:800px)]:space-y-2'
                  >
                    {navigation.map((item) => {
                      const isActive = firstSegment === item.href;
                      return (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            title={item.name}
                            className={classNames(
                              isActive
                                ? 'text-primary'
                                : 'text-gray-700 hover:text-primary ',
                              'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6'
                            )}
                          >
                            <item.icon
                              className={classNames(
                                isActive
                                  ? 'text-primary'
                                  : 'text-gray-400 group-hover:text-primary',
                                'h-5 w-5 shrink-0'
                              )}
                              aria-hidden='true'
                              title={item.name}
                            />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              </ul>
            </nav>
          </div>
          <div className='flex shrink-0 flex-col items-center gap-y-1 py-2 text-sm font-semibold leading-6 text-gray-900 hover:cursor-pointer [@media(max-height:800px)]:gap-y-0.5 [@media(max-height:800px)]:py-1'>
            <ul
              role='list'
              className='-mx-2 space-y-0.5 [@media(max-height:800px)]:space-y-0'
            >
              <li>
                <ModeToggle />
              </li>
              <li>
                <NotificationsPopover />
              </li>
              <li>
                <FeaturebaseFeedbackWidget />
              </li>
            </ul>
            <UserAvatarMenu />
          </div>
        </div>
      </div>
      <main className='ml-14'>
        <Outlet />
      </main>
    </>
  );
}
